/**
 * @module app.shared
 */

import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { IFormatter, INumberFormatOptions } from 'app/shared/lib/formatting/formatting-types';
import { getResourceFormat, getLanguageLocale } from 'app/shared/lib/formatting/formatting-utilities';
import { EnvironmentService } from 'app/shared/services/environment-service';

export abstract class BaseNumericFormatter implements IFormatter {

    protected readonly resourceKeyPrefix: string = 'ui:formats.number';
    protected readonly numberLocaleKey: string = 'ui:locales.number';

    constructor(protected readonly env: EnvironmentService, protected readonly i18n: I18N) { }

    public formatValue(value: unknown, options?: INumberFormatOptions) {
        const formatString = getResourceFormat(this.i18n, this.resourceKeyPrefix, options);
        const localeCode = getLanguageLocale(this.i18n, this.numberLocaleKey, options);

        let number: number;
        if(typeof value === 'number') {
            number = value;
        }
        else {
            number = parseFloat(`${value}`);
        }

        if(!Number.isFinite(number)) { return null; }

        if(formatString) {
            const currency = options && options.currency || this.env.currency || 'USD';
            const subFormats = formatString.split(';');
            let subFormat = subFormats[0];
            if(subFormats.length === 2) {
                subFormat = (number >= 0) ? subFormats[0] : subFormats[1];
            }
            else if(subFormats.length > 2) {
                subFormat =
                    (number > 0) ? subFormats[0] :
                    (number < 0) ? subFormats[1] :
                    subFormats[2];
            }
            if(subFormats.length !== 1) {
                // when specifying a negative format, the absolute value is used and the format must specify a negative sign if desired
                number = Math.abs(number);
            }
            return this.formatNumber(number, localeCode, subFormat.trim(), currency) || null;
        }
        else {
            return number.toLocaleString(localeCode);
        }
    }

    protected formatNumber(value: number, locale: string, format: string, currency?: string): string | null {
        if(!format) {
            return null;
        }

        /**
         * Groups: *
         * 0: Entire Match
         * 1: Currency `$$$` (with whitespace)
         * 2: Integer Digits `#0,`
         * 3: Decimal `.`
         * 4: Fraction Digits `#0,`
         * 5: Percent `%` (with whitespace)
         */
        const numberFormatRegex = /(\$*\s*)([0#,]+)(\.?)([0#,]*)(\s*\%?)/;

        const groups = numberFormatRegex.exec(format);
        if(!groups) {
            return format;
        }

        const prefix = format.substring(0, groups.index);
        const suffix = format.substring(groups.index + groups[0].length);

        let maxBefore = 0, minBefore = 0, maxAfter = 0, minAfter = 0;
        let hasGroup = false, currencyDisplay = '', style = 'decimal';

        const percentSuffix = groups[5].trim();
        if(percentSuffix.length >= 1) {
            style = 'percent';
        }

        const currencyPrefix = groups[1].trim();
        if(currencyPrefix.length === 1) {
            currencyDisplay = 'symbol'
            style = 'currency';
        }
        else if(currencyPrefix.length === 2) {
            currencyDisplay = 'code';
            style = 'currency';
        }
        else if(currencyPrefix.length >= 3) {
            currencyDisplay = 'name';
            style = 'currency';
        }

        const integerFormat = groups[2];
        const decimalFormat = groups[4];

        for(let index = integerFormat.length; index >= 0; index--) {
            const ch = integerFormat.charAt(index);
            if(ch === '#') {
                // # is an optional digit
                maxBefore++;
            }
            else if(ch === '0') {
                // 0 is a required digit
                maxBefore++;
                minBefore = maxBefore;
            }
            else if(ch === ',') {
                hasGroup = true;
            }
        }
        for(let index = 0; index < decimalFormat.length; index++) {
            const ch = decimalFormat.charAt(index);
            if(ch === '#') {
                // # is an optional digit
                maxAfter++;
            }
            else if(ch === '0') {
                // 0 is a required digit
                maxAfter++;
                minAfter = maxAfter;
            }
            else if(ch === ',') {
                hasGroup = true;
            }
        }

        const numberOptions: Intl.NumberFormatOptions = {
            minimumIntegerDigits: Math.min(21, Math.max(1, minBefore)),
            minimumFractionDigits: Math.min(20, Math.max(0, minAfter)),
            maximumFractionDigits: Math.min(20, Math.max(0, maxAfter)),
            useGrouping: hasGroup,
            style: style,
            currencyDisplay: currencyDisplay || undefined,
            currency: currency
        };

        const numberFormat = new Intl.NumberFormat(locale, numberOptions);
        const formatted = numberFormat.format(value);

        return `${prefix}${formatted}${suffix}`;
    }

}

@autoinject
export class NumberFormatter extends BaseNumericFormatter implements IFormatter {

    protected readonly resourceKeyPrefix: string = 'ui:formats.number';

    constructor(env: EnvironmentService, i18n: I18N) {
        super(env, i18n);
    }

}

@autoinject
export class DecimalFormatter extends BaseNumericFormatter implements IFormatter {

    protected readonly resourceKeyPrefix: string = 'ui:formats.decimal';

    constructor(env: EnvironmentService, i18n: I18N) {
        super(env, i18n);
    }

}

@autoinject
export class IntegerFormatter extends BaseNumericFormatter implements IFormatter {

    protected readonly resourceKeyPrefix: string = 'ui:formats.integer';

    constructor(env: EnvironmentService, i18n: I18N) {
        super(env, i18n);
    }

}

@autoinject
export class PercentFormatter extends BaseNumericFormatter implements IFormatter {

    protected readonly resourceKeyPrefix: string = 'ui:formats.percent';

    constructor(env: EnvironmentService, i18n: I18N) {
        super(env, i18n);
    }

}

@autoinject
export class CurrencyFormatter extends BaseNumericFormatter implements IFormatter {

    protected readonly resourceKeyPrefix: string = 'ui:formats.currency';

    constructor(env: EnvironmentService, i18n: I18N) {
        super(env, i18n);
    }

}
